import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Action } from '../../types/toolbar';
import { Toolbar } from '../../components/ui/toolbar/toolbar.component';
import CustomModal from '../../components/ui/modal/modal.component';
import { EditUser } from '../../workspaces/tenantConfig/editUser/editUser.component';
import { RemoveUser } from '../../workspaces/tenantConfig/removeUser/removeUser.component';
import { useCRUD } from '../../api/crud';
import { USERS_URL, useReactivateUser } from '../../api/users';
import { User } from '../../types/users';
import { useAuthContext } from '../../contexts/auth/auth.hooks';
import { useHasPermission } from '../../contexts/auth';
import { SUCCESS_SNACKBAR, ERROR_SNACKBAR } from '../../consts';

export const TenantUserActions = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams<{ id: string }>();
  const { get } = useCRUD(USERS_URL);
  const { reactivate } = useReactivateUser();
  const { currentUser } = useAuthContext();
  const [editUserOpened, setEditUserOpened] = useState<boolean>(false);
  const [removeUserOpened, setRemoveUserOpened] = useState<boolean>(false);
  const [user, setUser] = useState<User | undefined>(undefined);
  const canChangeUser = useHasPermission("users.change_user");
  const canDeleteUser = useHasPermission("users.delete_user");

  const isCurrentUser = user?.id === currentUser?.id;
  const isInactiveUser = user && !user.is_active;

  const fetchUser = useCallback(async () => {
    if (!id) return;

    const result = await get(id);

    if (result?.success && result?.data) {
      setUser(result.data);
    }
  }, [id, get]);

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  const handleReactivate = useCallback(async () => {
    if (!user?.id) return;
    const result = await reactivate(user.id);
    if (result?.success) {
      window.dispatchEvent(new Event('tenantUpdated'));
      enqueueSnackbar(t('User re-activated successfully'), SUCCESS_SNACKBAR);
      fetchUser();
    } else {
      enqueueSnackbar(t('Failed to re-activate user'), ERROR_SNACKBAR);
    }
  }, [user?.id, reactivate, enqueueSnackbar, t, fetchUser]);

  const actions: Action[] = [
    {
      icon: <EditIcon />,
      label: t('Edit user'),
      onClick: () => {
        setEditUserOpened(true);
      },
      disabled: !canChangeUser || isCurrentUser || isInactiveUser,
    },
    {
      icon: <PersonAddIcon />,
      label: t('Re-activate user'),
      onClick: handleReactivate,
      disabled: !canChangeUser || !isInactiveUser,
    },
    {
      icon: <DeleteIcon />,
      label: t('Remove from workspace'),
      onClick: () => {
        setRemoveUserOpened(true);
      },
      disabled: !canDeleteUser,
      color: "var(--errorColor)",
    },
  ];

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={editUserOpened}
        setOpened={setEditUserOpened}
        title={t('Edit user')}
        content={
          <EditUser
            user={user}
            onSuccess={() => {
              setEditUserOpened(false);
              fetchUser();
            }}
          />
        }
      />
      <CustomModal
        opened={removeUserOpened}
        setOpened={setRemoveUserOpened}
        title={t('Remove user from workspace')}
        content={
          <RemoveUser
            user={user}
            onSuccess={() => {
              setRemoveUserOpened(false);
            }}
          />
        }
      />
    </>
  );
};
